import { useEffect, useState, useSyncExternalStore } from "react";
import { KolejkaAutozapisu, type MigawkaZapisu } from "../lib/autozapis.js";
import type { MagazynSlotow } from "../lib/szkiceRecenzji.js";

/** Po tylu ms przerwy w pisaniu tekst leci na serwer (blur wysyla od razu). */
export const OPOZNIENIE_TEKSTU_MS = 800;

/**
 * Autozapis dla widoku recenzenta: opakowanie KolejkaAutozapisu (lib/autozapis.ts) w React.
 *
 * Hook niczego nie czyta z serwera i nie zna wartosci pol: pola trzymaja szkice w stanie lokalnym, wiec zaden zapis
 * ani odpowiedz nie nadpisze tekstu, ktory uzytkownik wlasnie pisze. Hook dostarcza trzy rzeczy:
 *   - migawke stanu ("idle" | "saving" | "saved" | "error" plus godzina ostatniego zapisu),
 *   - ostrzezenie `beforeunload`, dopoki cokolwiek jest niezapisane (czeka, leci albo sie nie udalo),
 *   - wyslanie WSZYSTKIEGO, co niezapisane (takze nieudanych zapisow), przy ukryciu karty i przy odmontowaniu widoku.
 *
 * `beforeunload` pokrywa tylko zamkniecie karty i przeladowanie, a nie przejscie do innej rozmowy w aplikacji. Dlatego
 * opcjonalny `magazyn` trzyma kopie szkicow, ktorych serwer nie przyjal: wroca po ponownym wejsciu na rozmowe.
 */
export function useAutozapis(magazyn: MagazynSlotow | null = null): {
  migawka: MigawkaZapisu;
  zaplanuj: KolejkaAutozapisu["zaplanuj"];
  splucz: KolejkaAutozapisu["splucz"];
  splukajWszystko: KolejkaAutozapisu["splukajWszystko"];
  ponow: KolejkaAutozapisu["ponow"];
  poczekaj: KolejkaAutozapisu["poczekaj"];
} {
  const [kolejka] = useState(() => new KolejkaAutozapisu(undefined, magazyn));
  const migawka = useSyncExternalStore(kolejka.subskrybuj, kolejka.migawka);

  useEffect(() => {
    // Przegladarki ignoruja wlasny tekst, ale pokazanie okna wymaga preventDefault (i returnValue w starszych).
    const przedZamknieciem = (zdarzenie: BeforeUnloadEvent): void => {
      if (!kolejka.migawka().niezapisane) return;
      zdarzenie.preventDefault();
      zdarzenie.returnValue = "";
    };
    // Karta schowana (przelaczenie, zamykanie) to ostatnia pewna chwila na wyslanie tego, co czeka na debounce albo
    // na ponowienie po bledzie.
    const przyUkryciu = (): void => {
      if (document.visibilityState === "hidden") kolejka.splukajWszystko();
    };

    window.addEventListener("beforeunload", przedZamknieciem);
    document.addEventListener("visibilitychange", przyUkryciu);
    return () => {
      window.removeEventListener("beforeunload", przedZamknieciem);
      document.removeEventListener("visibilitychange", przyUkryciu);
      // Przejscie do innego widoku nie gubi tekstu, ktory czekal na koniec pisania ani tego, co sie nie zapisalo.
      kolejka.splukajWszystko();
    };
  }, [kolejka]);

  // Funkcje sa stale (pola kolejki), zmienia sie tylko migawka: konsumenci biora do zaleznosci same funkcje,
  // dzieki czemu zmiana statusu zapisu nie odswieza wszystkich pol naraz.
  return {
    migawka,
    zaplanuj: kolejka.zaplanuj,
    splucz: kolejka.splucz,
    splukajWszystko: kolejka.splukajWszystko,
    ponow: kolejka.ponow,
    poczekaj: kolejka.poczekaj,
  };
}
